'use client'

import { clsx } from 'clsx'
import {
  type ComponentProps,
  type ElementType,
  type FC,
  useMemo,
  useState,
} from 'react'
import { FormattedMessage } from 'react-intl'

import {
  type GkAirRecommendedProductAttributesWeb,
  type GkAirRecommendedProductDataWeb,
  type GkAirRecommendedProductWeb,
} from '@redteclab/api/clients/crosssellers-proxy-service'

import { Button } from 'base-ui'

import { EXPERIMENTS_TRACK_EVENT_RECOMMENDATION_SHOW_MORE_PRODUCTS_CLICK } from '../../../experiments/eventTracking'
import { useGlobalConfigContext } from '../../../global-config'
import { recommendationProductIsRenderable } from '../../model'
import { recommendationExperimentsTrackRecommendationEvent } from '../../recommendation-experiments'
import { useRecommendationWidgetContext } from '../RecommendationAreaProvider'
import { RecommendationProductCard } from '../RecommendationProductCard'

type RecommendationGridProps = Omit<ComponentProps<'div'>, 'children'> & {
  // rename according to new recommendation GTM names
  onGtmDataLayerPush?: (
    widgetProductId: GkAirRecommendedProductDataWeb['UID'],
    trackingToken?: GkAirRecommendedProductAttributesWeb['trackingtoken'],
  ) => void
  products?: GkAirRecommendedProductWeb[]
  slotProps?: {
    itemList: {
      className?: string
    }
  }
  slots?: {
    itemContent?: ElementType
  }
}

const MOBILE_SKELETON_ITEM_COUNT = 4
const PRODUCTS_PER_PAGE = 8

export const RecommendationGrid: FC<RecommendationGridProps> = ({
  className,
  onGtmDataLayerPush,
  products,
  slotProps,
  slots,
  ...rest
}) => {
  const { locale } = useGlobalConfigContext()
  const {
    saveUdpProductClickAttribution,
    serviceId,
    trackUdpProductImpression,
    widgetId,
  } = useRecommendationWidgetContext()

  const [itemVisibleCount, setItemVisibleCount] = useState(PRODUCTS_PER_PAGE)

  const [items, totalProductsCount] = useMemo((): [
    items: GkAirRecommendedProductWeb[] | number[],
    count: number,
  ] => {
    /**
     * number[] will be returned to show skeleton items before csw call returns actual items
     */

    if (!products) {
      return [Array.from({ length: PRODUCTS_PER_PAGE }).keys().toArray(), 0]
    }

    const availableProducts = products.filter(({ data }) =>
      recommendationProductIsRenderable({
        locale,
        product: data,
      }),
    )

    return [
      availableProducts.slice(0, itemVisibleCount),
      availableProducts.length,
    ]
  }, [itemVisibleCount, locale, products])

  const ProductComponent = slots?.itemContent ?? RecommendationProductCard

  return (
    <div
      className={clsx(
        'flex flex-col items-center gap-4 tablet:gap-6',
        className,
      )}
      {...rest}
    >
      <ul
        className={clsx(
          slotProps?.itemList.className ??
            'grid w-full grid-cols-2 gap-4 tablet:grid-cols-4 tablet:gap-y-6',
        )}
      >
        {items.map((item, index) => {
          return (
            <li
              key={typeof item === 'number' ? item : item.data.UID}
              ref={
                typeof item === 'number'
                  ? undefined
                  : trackUdpProductImpression?.({
                      position: index + 1,
                      product: item,
                    })
              }
            >
              <ProductComponent
                className={clsx(
                  'h-full',
                  typeof item === 'number' &&
                    index >= MOBILE_SKELETON_ITEM_COUNT &&
                    'hidden tablet:block',
                )}
                onProductLinkClick={
                  typeof item === 'number'
                    ? undefined
                    : (): void => {
                        saveUdpProductClickAttribution?.(item.data.UID)
                        onGtmDataLayerPush?.(
                          item.data.UID,
                          item.attributes.trackingtoken,
                        )
                      }
                }
                product={typeof item === 'number' ? undefined : item.data}
              />
            </li>
          )
        })}
      </ul>
      {itemVisibleCount < totalProductsCount && (
        <Button
          color="secondary"
          onClick={() => {
            recommendationExperimentsTrackRecommendationEvent({
              eventKey:
                EXPERIMENTS_TRACK_EVENT_RECOMMENDATION_SHOW_MORE_PRODUCTS_CLICK,
              serviceId,
              widgetId,
            })
            setItemVisibleCount((prevState) => prevState + PRODUCTS_PER_PAGE)
          }}
          size="large"
        >
          <FormattedMessage id="general.label.loadMoreProducts" />
        </Button>
      )}
    </div>
  )
}
